// GENERATED FILE — do not edit by hand.
// Source: backend/src/services/vmix/script.js
// Regenerate: cd backend && node scripts/sync-vmix-libs.mjs
//
// The backend and frontend deploy separately, so the browser editor needs its
// own copy of the parser. vmix-libs.test.js fails the build if this drifts.

// Model a vMix script as an ordered list of rows, so the same script can be
// edited as raw VB.NET or as a rundown-style action list.
//
// CANONICAL COPY. The frontend gets a generated duplicate at
// frontend/src/lib/vmixScript.js — the two packages deploy separately (Railway
// and Vercel) so neither can import the other at runtime. Edit this file and run
// `cd backend && node scripts/sync-vmix-libs.mjs`; vmix-libs.test.js fails the
// build if the copies drift.
//
// The hard requirement is that flipping between the two views is LOSSLESS. A
// script is real VB.NET — loops, Dim, string building — and only some of it is
// a flat list of vMix calls. So every line ends up in a row, and a row that we
// did not fully understand is kept verbatim as `raw`. A row that the operator
// has not edited is re-emitted from its original source text, byte for byte;
// only edited rows are regenerated. Load → Actions → Raw → download therefore
// cannot silently reformat a script that was never touched.
//
// Recognised row kinds:
//   function  API.Function(…) in named or positional form, a legacy
//             `Function=X&Input=1` shortcut line, or a three-line
//             WebRequest block addressing another vMix over its Web API
//   sleep     sleep(500) / Sleep 1000
//   comment   'anything
//   blank     an empty line
//   raw       everything else — VB control flow, Dim, unparseable calls

let nextRowId = 1;
const rowId = () => `row-${nextRowId++}`;

// API.Function(functionName, input, value, duration, selectedName, selectedIndex)
// — the positional order documented in the vMix VB.NET scripting reference.
export const POSITIONAL_ARGS = ['Input', 'Value', 'Duration', 'SelectedName', 'SelectedIndex'];

// ---------------------------------------------------------------- parsing --

export function parseScript(code) {
  const lines = (code ?? '').split('\n');
  const rows = [];

  for (let i = 0; i < lines.length; i++) {
    const web = matchWebRequestBlock(lines, i);
    if (web) {
      rows.push(web.row);
      i = web.lastLine;
      continue;
    }
    rows.push(parseLine(lines[i]));
  }

  return rows;
}

function base(raw, kind, extra) {
  // `pristine` means "raw is still authoritative" — serialize() will emit it
  // untouched. editRow() clears it.
  return { id: rowId(), kind, raw, pristine: true, ...extra };
}

function parseLine(raw) {
  if (/^\s*$/.test(raw)) return base(raw, 'blank', {});

  const comment = /^(\s*)'(.*)$/.exec(raw);
  if (comment) return base(raw, 'comment', { indent: comment[1], text: comment[2] });

  const sleep = /^(\s*)sleep\s*(?:\(\s*(\d+)\s*\)|(\d+))\s*$/i.exec(raw);
  if (sleep) {
    return base(raw, 'sleep', {
      indent: sleep[1],
      ms: Number(sleep[2] ?? sleep[3]),
      // Preserve which of the two spellings this script uses.
      style: sleep[2] != null ? 'paren' : 'bare',
    });
  }

  const api = matchApiFunction(raw);
  if (api) return base(raw, 'function', api);

  const legacy = matchLegacyLine(raw);
  if (legacy) return base(raw, 'function', legacy);

  return base(raw, 'raw', {});
}

// API.Function("Fn", Input:="1", Value:="x")  or  API.Function("Fn","1","x")
//
// Returns null unless every argument is a plain string literal. A call whose
// argument is a variable — API.Function("ListAdd", "2", filePath) inside a
// For Each — stays a raw row, because rendering it as a fixed value and then
// regenerating it would quietly break the loop it lives in.
function matchApiFunction(raw) {
  const m = /^(\s*)API\.Function\s*\(([\s\S]*)\)\s*$/i.exec(raw);
  if (!m) return null;

  const args = splitArgs(m[2]);
  if (args.length === 0) return null;

  const fnLit = stringLiteral(args[0]);
  if (fnLit == null) return null;

  const params = {};
  // Named arguments this editor has no column for — Channel:= on the Replay
  // functions, for instance. The documented signature is only six parameters
  // deep but vMix accepts more, so rather than guess at the full list we carry
  // anything we don't have a field for straight through to the output.
  const extras = [];
  let style = 'positional';
  let positionalIndex = 0;

  for (const arg of args.slice(1)) {
    const named = /^([A-Za-z]+)\s*:=\s*([\s\S]+)$/.exec(arg);
    if (named) {
      const lit = stringLiteral(named[2]);
      if (lit == null) return null;                 // Value:=someVariable
      const key = POSITIONAL_ARGS.find(p => p.toLowerCase() === named[1].toLowerCase());
      if (key) params[key] = lit;
      else extras.push([named[1], lit]);
      style = 'named';
    } else {
      const lit = stringLiteral(arg);
      if (lit == null) return null;                 // bare variable
      const key = POSITIONAL_ARGS[positionalIndex++];
      if (!key) return null;                        // more args than the signature has
      params[key] = lit;
    }
  }

  return {
    indent: m[1],
    style,
    target: null,                                   // null = the vMix running the script
    fn: fnLit,
    input: params.Input || '',
    value: params.Value || '',
    duration: params.Duration || '',
    extras,
  };
}

// The pre-scripting shortcut form, still valid in a script and still all over
// older presets: Function=SetVolumeFade&Value=0,1000&Input=1
function matchLegacyLine(raw) {
  const m = /^(\s*)Function=([A-Za-z0-9_]+)((?:&[A-Za-z]+=[^&]*)*)\s*$/.exec(raw);
  if (!m) return null;

  const params = {};
  const extras = [];
  for (const pair of m[3].split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    if (eq === -1) continue;
    const key = pair.slice(0, eq);
    const val = pair.slice(eq + 1);
    if (['input', 'value', 'duration'].includes(key.toLowerCase())) params[key.toLowerCase()] = val;
    else extras.push([key, val]);
  }

  return {
    indent: m[1],
    style: 'legacy',
    target: null,
    fn: m[2],
    input: params.input || '',
    value: params.value || '',
    duration: params.duration || '',
    extras,
  };
}

// The idiom for driving a *different* vMix from a script — there is no
// API.Function equivalent, so operators hand-roll an HTTP call:
//
//   Dim client1 = WebRequest.create("http://10.0.0.2:8088/api/?Function=X&Input=13")
//   Dim response1 = client1.GetResponse()
//   response1.Close
//
// Treated as one row so the actions view can show it as "vMix OB1 → OverlayInput8In".
function matchWebRequestBlock(lines, i) {
  const create = /^(\s*)Dim\s+(\w+)\s*=\s*WebRequest\.create\s*\(\s*"([^"]*)"\s*\)\s*$/i.exec(lines[i] ?? '');
  if (!create) return null;
  const getResp = new RegExp(`^\\s*Dim\\s+(\\w+)\\s*=\\s*${create[2]}\\.GetResponse\\s*\\(\\s*\\)\\s*$`, 'i')
    .exec(lines[i + 1] ?? '');
  if (!getResp) return null;
  if (!new RegExp(`^\\s*${getResp[1]}\\.Close\\s*$`, 'i').test(lines[i + 2] ?? '')) return null;

  const url = parseVmixApiUrl(create[3]);
  if (!url) return null;

  return {
    lastLine: i + 2,
    row: base(lines.slice(i, i + 3).join('\n'), 'function', {
      indent: create[1],
      style: 'webrequest',
      clientVar: create[2],
      respVar: getResp[1],
      target: { host: url.host, port: url.port },
      fn: url.fn,
      input: url.input,
      value: url.value,
      duration: url.duration,
      extras: url.extras,
    }),
  };
}

function parseVmixApiUrl(url) {
  const m = /^https?:\/\/([^/:]+)(?::(\d+))?\/api\/?\?(.*)$/i.exec(url);
  if (!m) return null;
  const params = {};
  const extras = [];
  for (const pair of m[3].split('&')) {
    const eq = pair.indexOf('=');
    if (eq === -1) continue;
    const key = decodeURIComponent(pair.slice(0, eq));
    const val = decodeURIComponent(pair.slice(eq + 1));
    if (['function', 'input', 'value', 'duration'].includes(key.toLowerCase())) params[key.toLowerCase()] = val;
    else extras.push([key, val]);
  }
  if (!params.function) return null;
  return {
    host: m[1],
    port: m[2] ? Number(m[2]) : 8088,
    fn: params.function,
    input: params.input || '',
    value: params.value || '',
    duration: params.duration || '',
    extras,
  };
}

// Split an argument list on top-level commas, ignoring commas inside strings.
// vMix values are full of them — "55,2500" for SetVolumeFade, "1,2,1000" for
// SwapLayerAnimated.
function splitArgs(src) {
  const out = [];
  let depth = 0;
  let inString = false;
  let current = '';
  if (src.trim() === '') return out;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inString) {
      current += ch;
      if (ch === '"') {
        if (src[i + 1] === '"') { current += '"'; i++; }   // VB escapes a quote by doubling it
        else inString = false;
      }
      continue;
    }
    if (ch === '"') { inString = true; current += ch; continue; }
    if (ch === '(') depth++;
    if (ch === ')') depth--;
    if (ch === ',' && depth === 0) { out.push(current.trim()); current = ''; continue; }
    current += ch;
  }
  out.push(current.trim());
  return out;
}

// The contents of a "…" literal, or null if the argument is anything else.
function stringLiteral(arg) {
  const t = arg.trim();
  if (t.length < 2 || t[0] !== '"' || t[t.length - 1] !== '"') return null;
  const inner = t.slice(1, -1);
  // An unescaped quote inside means this is a concatenation, not one literal.
  if (inner.replace(/""/g, '').includes('"')) return null;
  return inner.replace(/""/g, '"');
}

// -------------------------------------------------------------- serialising --

export function serializeScript(rows) {
  return rows.map(serializeRow).join('\n');
}

export function serializeRow(row) {
  if (row.pristine) return row.raw;

  const indent = row.indent ?? '';
  switch (row.kind) {
    case 'blank':
      return '';
    case 'comment':
      return `${indent}'${row.text}`;
    case 'sleep':
      return row.style === 'bare' ? `${indent}Sleep ${row.ms}` : `${indent}sleep(${row.ms})`;
    case 'function':
      return serializeFunction(row);
    default:
      return row.raw;
  }
}

function serializeFunction(row) {
  const indent = row.indent ?? '';

  if (row.style === 'webrequest') {
    const q = new URLSearchParams();
    q.set('Function', row.fn);
    if (row.input) q.set('Input', row.input);
    if (row.value) q.set('Value', row.value);
    if (row.duration) q.set('Duration', row.duration);
    const port = row.target?.port ?? 8088;
    const url = `http://${row.target?.host}:${port}/api/?${q.toString()}`;
    const client = row.clientVar || 'client1';
    const resp = row.respVar || 'response1';
    return [
      `${indent}Dim ${client} = WebRequest.create("${url}")`,
      `${indent}Dim ${resp} = ${client}.GetResponse()`,
      `${indent}${resp}.Close`,
    ].join('\n');
  }

  if (row.style === 'legacy') {
    let out = `${indent}Function=${row.fn}`;
    if (row.input) out += `&Input=${row.input}`;
    if (row.value) out += `&Value=${row.value}`;
    if (row.duration) out += `&Duration=${row.duration}`;
    for (const [k, v] of row.extras || []) out += `&${k}=${v}`;
    return out;
  }

  const lit = v => `"${String(v).replace(/"/g, '""')}"`;

  const extras = (row.extras || []).map(([k, v]) => `${k}:=${lit(v)}`);

  if (row.style === 'positional' && extras.length === 0) {
    // Positional args can't be skipped, so a value with no input needs the
    // empty slot spelled out.
    const vals = [row.input, row.value, row.duration];
    while (vals.length && !vals[vals.length - 1]) vals.pop();
    return `${indent}API.Function(${[lit(row.fn), ...vals.map(v => lit(v || ''))].join(',')})`;
  }

  const parts = [lit(row.fn)];
  if (row.input) parts.push(`Input:=${lit(row.input)}`);
  if (row.value) parts.push(`Value:=${lit(row.value)}`);
  if (row.duration) parts.push(`Duration:=${lit(row.duration)}`);
  parts.push(...extras);
  return `${indent}API.Function(${parts.join(', ')})`;
}

// ---------------------------------------------------------------- editing --

/** Apply a patch to a row, dropping `pristine` so it is regenerated on save. */
export function editRow(row, patch) {
  return { ...row, ...patch, pristine: false };
}

/** A fresh row, ready to insert. `target` is null for local, {host,port} remote. */
export function makeFunctionRow({ fn = '', input = '', value = '', duration = '', target = null }) {
  return {
    id: rowId(),
    kind: 'function',
    pristine: false,
    indent: '',
    style: target ? 'webrequest' : 'named',
    // Numbered on serialise-into-script so two blocks in one script don't
    // collide on `client1`; see renumberWebRequestVars.
    clientVar: 'client1',
    respVar: 'response1',
    target,
    fn, input, value, duration,
    extras: [],
  };
}

export function makeSleepRow(ms = 1000) {
  return { id: rowId(), kind: 'sleep', pristine: false, indent: '', ms, style: 'paren' };
}

export function makeCommentRow(text = '') {
  return { id: rowId(), kind: 'comment', pristine: false, indent: '', text: text ? ` ${text}` : '' };
}

export function makeRawRow(raw = '') {
  return { id: rowId(), kind: 'raw', pristine: true, raw };
}

export function cloneRow(row) {
  // A clone is a new row that must be regenerated, not a second copy of the
  // same source text — otherwise editing it would not take effect.
  return { ...row, id: rowId(), pristine: row.kind === 'raw' };
}

/**
 * Ensure no two WebRequest rows declare the same variable.
 *
 * `Dim client1` twice in one scope is a VB.NET compile error, and a script that
 * won't compile does nothing at all — no error on screen, the operator's cue
 * just doesn't fire. That is easy to cause by accident here: retargeting two
 * local calls to another vMix turns both into blocks that would otherwise both
 * be named `client1`.
 *
 * Only colliding rows are renamed. Scripts that name their variables something
 * other than clientN are left exactly as the operator wrote them, because
 * renaming on every edit would churn lines nobody touched.
 */
export function dedupeWebRequestVars(rows) {
  const isBlock = r => r.kind === 'function' && r.style === 'webrequest';
  const lower = v => (v || '').toLowerCase();

  // Pass 1 — first row to claim a name keeps it; anything after that must move.
  const claimed = new Set();
  const mustRename = new Set();
  for (const row of rows) {
    if (!isBlock(row)) continue;
    if (claimed.has(lower(row.clientVar)) || claimed.has(lower(row.respVar)) || !row.clientVar || !row.respVar) {
      mustRename.add(row.id);
    } else {
      claimed.add(lower(row.clientVar));
      claimed.add(lower(row.respVar));
    }
  }
  if (mustRename.size === 0) return rows;

  // Pass 2 — hand the movers the lowest clientN/responseN pair nobody holds.
  let n = 0;
  const free = () => {
    do { n++; } while (claimed.has(`client${n}`) || claimed.has(`response${n}`));
    claimed.add(`client${n}`);
    claimed.add(`response${n}`);
    return n;
  };

  return rows.map(row => {
    if (!isBlock(row) || !mustRename.has(row.id)) return row;
    const i = free();
    return { ...row, clientVar: `client${i}`, respVar: `response${i}`, pristine: false };
  });
}

// ------------------------------------------------------------- describing --

/** Human label for a row's target, resolving a host:port to a known instance. */
export function targetLabel(row, instances) {
  if (!row.target) return 'This vMix';
  const match = (instances || []).find(
    i => i.host === row.target.host && Number(i.port) === Number(row.target.port),
  );
  if (match) return match.label;
  return `${row.target.host}:${row.target.port}`;
}

/** Count of rows that are real vMix calls — what the script list shows. */
export function countFunctions(rows) {
  return rows.filter(r => r.kind === 'function').length;
}
